/* eslint-disable prettier/prettier */

// Guaranteed Payment Exposure by Originating Attorney.
//
// Read-only aggregation over the same inputs the Payments sheet already uses:
//   Guaranteed (payments.js column L) = Invoiced Amount Atty * Guaranteed %, and only
//                                       when the attorney is NOT the matter originating
//                                       attorney (column I = "N")
//   Previously Paid (column M)        = persisted payment GuaranteedPrevPaid
//   Collected (column F)              = bill.state === "paid"
//   Originating Atty                  = clio matter originating_attorney.name
//
// For every uncollected invoice it sums the Guaranteed amounts already flagged as paid
// and groups them under the originating attorney of the matter, i.e. the credit the firm
// has extended against that attorney book of business.

const DEFAULT_GUARANTEED = 0.55;

// Same resolution matters.js uses when it prints the custom Guaranteed % for a matter.
const getPersistedMatter = (persistedData, matterDisplayNumber, attorney) => {
    const empty = {
        Guaranteed: "",
        "On-Collection": "",
        Responsible: "",
        Originating: "",
    };

    if (!persistedData || !persistedData.matters || !(matterDisplayNumber in persistedData.matters)) {
        return empty;
    }

    const matterData = persistedData.matters[matterDisplayNumber];

    if (Array.isArray(matterData)) {
        return matterData.find((item) => item && item.attorney === attorney) || empty;
    }

    if (matterData && (matterData.attorney === attorney || !matterData.attorney)) {
        return matterData;
    }

    return empty;
};

// Same resolution payments.js uses to read the manual Previously Paid flag.
const getPersistedPayment = (persistedData, billNumber, attorney) => {
    const empty = {
        InvoicedAmountAtty: 0,
        CustomAdjustment: 0,
        CustomAdjustmentPrevPaid: "N",
        GuaranteedPrevPaid: "N",
    };

    if (!persistedData || !persistedData.payments || !(billNumber in persistedData.payments)) {
        return empty;
    }

    const paymentData = persistedData.payments[billNumber];

    if (Array.isArray(paymentData)) {
        return paymentData.find((payment) => payment && payment.attorney === attorney) || empty;
    }

    return paymentData && paymentData.attorney === attorney ? paymentData : empty;
};

const getGuaranteedPercentage = (persistedMatter) => {
    const percentage = Number(persistedMatter.Guaranteed);

    return persistedMatter.Guaranteed && Number.isFinite(percentage)
        ? percentage
        : DEFAULT_GUARANTEED;
};

export const getGuaranteedExposure = (
    billingData,
    mattersData,
    clioActivitiesKeyed,
    persistedData
) => {
    const emptyResult = { attorneys: [], grandTotal: 0, matterCount: 0 };

    try {
        if (!billingData || !Array.isArray(billingData)) {
            console.error("Invalid billingData:", billingData);
            return emptyResult;
        }

        if (!mattersData || !Array.isArray(mattersData)) {
            console.error("Invalid mattersData:", mattersData);
            return emptyResult;
        }

        const activitiesKeyed =
            clioActivitiesKeyed && typeof clioActivitiesKeyed === "object" ? clioActivitiesKeyed : {};

        // Key the clio matters so each bill can find its originating attorney.
        const mattersKeyed = {};
        mattersData.forEach((matter) => {
            if (
                matter &&
                matter.display_number &&
                matter.originating_attorney &&
                typeof matter.originating_attorney === "object" &&
                matter.originating_attorney.name
            ) {
                mattersKeyed[matter.display_number] = matter;
            }
        });

        const exposureByAttorney = {};

        billingData.forEach((bill) => {
            try {
                if (
                    !bill ||
                    !bill.matters ||
                    !Array.isArray(bill.matters) ||
                    bill.matters.length === 0 ||
                    !bill.matters[0]
                ) {
                    return;
                }

                const matterDisplayNumber = bill.matters[0].display_number;
                if (!matterDisplayNumber) return;

                // Collected (Y/N) - only uncollected invoices carry exposure.
                if (bill.state === "paid") return;

                const matter = mattersKeyed[matterDisplayNumber];
                if (!matter) return;

                const originatingAttorney = matter.originating_attorney.name;

                // Attorneys with billed activity on this invoice are the attorneys who get a
                // Guaranteed row for it on the Payments sheet.
                const billActivities = activitiesKeyed[bill.number];
                if (!billActivities || typeof billActivities !== "object") return;

                Object.entries(billActivities).forEach(([attorney, invoicedAmountAtty]) => {
                    // Guaranteed only applies when the attorney is not the originating attorney.
                    if (!attorney || attorney === originatingAttorney) return;

                    // Previously Paid - the manual flag, read as-is.
                    const persisted = getPersistedPayment(persistedData, bill.number, attorney);
                    if (persisted.GuaranteedPrevPaid !== "Y") return;

                    const persistedMatter = getPersistedMatter(
                        persistedData,
                        matterDisplayNumber,
                        attorney
                    );
                    const guaranteed = +invoicedAmountAtty * getGuaranteedPercentage(persistedMatter);

                    if (!guaranteed || !Number.isFinite(guaranteed)) return;

                    if (!(originatingAttorney in exposureByAttorney)) {
                        exposureByAttorney[originatingAttorney] = { total: 0, matters: {} };
                    }

                    const attorneyExposure = exposureByAttorney[originatingAttorney];

                    if (!(matterDisplayNumber in attorneyExposure.matters)) {
                        attorneyExposure.matters[matterDisplayNumber] = {
                            displayNumber: matterDisplayNumber,
                            total: 0,
                            invoices: [],
                            paidAttorneys: {},
                        };
                    }

                    const matterExposure = attorneyExposure.matters[matterDisplayNumber];

                    matterExposure.paidAttorneys[attorney] =
                        (matterExposure.paidAttorneys[attorney] || 0) + guaranteed;
                    matterExposure.total += guaranteed;
                    attorneyExposure.total += guaranteed;

                    if (!matterExposure.invoices.includes(bill.number)) {
                        matterExposure.invoices.push(bill.number);
                    }
                });
            } catch (error) {
                console.error("Error processing bill for exposure:", bill, error);
            }
        });

        let matterCount = 0;

        const attorneys = Object.entries(exposureByAttorney)
            .map(([name, attorneyExposure]) => {
                const matters = Object.values(attorneyExposure.matters)
                    .map((matterExposure) => ({
                        ...matterExposure,
                        paidAttorneys: Object.entries(matterExposure.paidAttorneys)
                            .map(([paidName, amount]) => ({ name: paidName, amount }))
                            .sort((a, b) => b.amount - a.amount),
                    }))
                    .sort((a, b) => b.total - a.total);

                matterCount += matters.length;

                return { name, total: attorneyExposure.total, matters };
            })
            .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));

        const grandTotal = attorneys.reduce((total, attorney) => total + attorney.total, 0);

        return { attorneys, grandTotal, matterCount };
    } catch (error) {
        console.error("Error calculating guaranteed exposure:", error);
        return emptyResult;
    }
};
